import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { pageTabRouteKey } from '@/utils/pageTabRouteKey';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';

function pageTabsStorageKey(): string | null {
  const scope = getUserSessionScopeSuffix();
  return scope === '_guest' ? null : `aipd:page-tabs:v1${scope}`;
}

/** 全屏 Dashboard AI 时展示的快捷提示（与项目管理「新建项目」/侧栏「新对话」共用） */
export const SIDEBAR_NEW_CHAT_QUICK_PROMPTS = [
  {
    label: '项目类型',
    text: '这是什么类型的项目？（比如迭代性项目、全新型项目等）请帮我梳理并给出建议。',
  },
  {
    label: '文档内容',
    text: '我希望文档包含：项目介绍、时间规划、成员分工、风险分析、SOR 文档等，请按此帮我列出大纲。',
  },
  {
    label: '特别要求',
    text: '我还有一些特别要求与参考资料，请根据我补充的要点帮我整理并纳入项目说明。',
  },
] as const;

export type ShellPageTab = {
  key: string;
  label: string;
  to: any;
  closable: boolean;
  type?: 'route' | 'custom';
};

/** 全屏 AI 关闭后回填「创建项目」弹窗的草稿（由 DashboardView 消费） */
export type ExpandedProjectDraftPayload = {
  name?: string;
  code?: string;
  owner?: string;
  deliveryDate?: string;
  leadUserId?: string;
};

/** Dashboard / 侧栏创建项目对话就绪后，跳转 ProjectCreate 分屏页时的预填数据 */
export type PendingAiProjectCreateHydration = {
  structuredResult: Record<string, unknown>;
  answerText?: string;
  conversationId?: string;
};

/** 通用外壳：主导航、Dashboard 全屏 AI 等 */
export const useAppShellStore = defineStore('appShell', () => {
  /** UI 稿按顶栏 77→56 缩放：展开 81 / 收起 55 */
  const PRIMARY_RAIL_WIDTH = 81;
  const PRIMARY_RAIL_COLLAPSED_WIDTH = 55;

  /** 全屏居中 AI（任意路由；隐藏右侧固定面板占位，会话逻辑仍由 AiAssistantPanel 承担） */
  const dashboardExpandedAiComposer = ref(false);

  /** 全屏对话识别到足够创建草稿时写入，由 Dashboard 打开弹窗并清空 */
  const pendingExpandedProjectDraft = ref<ExpandedProjectDraftPayload | null>(null);

  /** 结构化信息已足够时由 AI 面板写入，ProjectCreate 挂载后消费并清空 */
  const pendingAiProjectCreateHydration = ref<PendingAiProjectCreateHydration | null>(null);

  /** 跳转分屏创建页前的全屏过渡（「开始自动填充项目信息」） */
  const showProjectAutofillIntro = ref(false);

  /** Dashboard 全屏内点击「表单创建」时递增，由 DashboardView 打开创建弹窗 */
  const openDashboardProjectFormTick = ref(0);

  /** 侧边栏是否缩进到图标模式 */
  const sidebarCollapsed = ref(false);

  /** 知识空间顶栏切换范围 */
  const knowledgeScope = ref<'personal' | 'company' | 'customer'>('personal');

  /** 递增以触发 AiAssistantPanel 新建会话（与全屏展开配合使用） */
  const newConversationTick = ref(0);

  /** 当前已打开页签 */
  const pageTabs = ref<ShellPageTab[]>([]);
  /** 当前激活页签 key */
  const activePageTabKey = ref('');
  /** 后台任务完成、待用户切回查看的页签（实心圆点） */
  const tabCompletionKeys = ref<Set<string>>(new Set());
  /** 页签内仍在运行的后台任务数（用户离开页签时展示转圈动画） */
  const tabRunningTaskCounts = ref<Record<string, number>>({});

  // 由 Layout 在认证身份确定后初始化；保留旧 key，换号时不能把旧标签写给新用户。
  let tabsStorageKey: string | null = null;

  function restorePageTabs(isValidRoute: (to: ShellPageTab['to']) => boolean) {
    const key = pageTabsStorageKey();
    if (!key || key === tabsStorageKey) return false;
    let restored = false;
    tabsStorageKey = null;
    clearPageTabs();
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (saved && Array.isArray(saved.pageTabs)) {
        restored = true;
        const keys = new Set<string>();
        pageTabs.value = saved.pageTabs.filter((tab: ShellPageTab | null) => {
          if (!tab || typeof tab.key !== 'string' || !tab.key ||
              typeof tab.label !== 'string' || typeof tab.closable !== 'boolean' ||
              (tab.type != null && tab.type !== 'route' && tab.type !== 'custom')) return false;
          const to = tab.to;
          const path = typeof to === 'string' ? to : to?.path;
          const hasTarget = (typeof path === 'string' && /^\/(?!\/)/.test(path)) ||
            (to && typeof to === 'object' && typeof to.name === 'string' && to.name.length > 0);
          if (!hasTarget || keys.has(tab.key)) return false;
          try {
            if (!isValidRoute(to)) return false;
          } catch {
            return false; // 已删除的路由或缺失必填参数仅丢弃当前项。
          }
          keys.add(tab.key);
          return true;
        });
        activePageTabKey.value = keys.has(saved.activePageTabKey)
          ? saved.activePageTabKey : pageTabs.value[0]?.key || '';
        dashboardExpandedAiComposer.value = !pageTabs.value.length;
      }
    } catch {
      // 缓存损坏或浏览器禁用存储时，仍按当前 URL 打开页面。
    }
    tabsStorageKey = key;
    return restored;
  }

  watch([pageTabs, activePageTabKey], () => {
    if (!tabsStorageKey) return;
    try {
      sessionStorage.setItem(tabsStorageKey, JSON.stringify({
        pageTabs: pageTabs.value.map(({ key, label, to, closable, type }) => ({ key, label, to, closable, type })),
        activePageTabKey: activePageTabKey.value,
      }));
    } catch {
      // quota / private mode：不阻断打开、关闭或切换标签。
    }
  }, { deep: true, flush: 'sync' });

  function setDashboardExpandedAiComposer(on: boolean) {
    dashboardExpandedAiComposer.value = on;
  }

  function setPendingExpandedProjectDraft(payload: ExpandedProjectDraftPayload | null) {
    pendingExpandedProjectDraft.value = payload;
  }

  function setPendingAiProjectCreateHydration(payload: PendingAiProjectCreateHydration | null) {
    pendingAiProjectCreateHydration.value = payload;
  }

  function startProjectAutofillIntro() {
    showProjectAutofillIntro.value = true;
  }

  function endProjectAutofillIntro() {
    showProjectAutofillIntro.value = false;
  }

  function requestOpenDashboardProjectForm() {
    openDashboardProjectFormTick.value += 1;
  }

  function setSidebarCollapsed(on: boolean) {
    sidebarCollapsed.value = on;
  }

  function setKnowledgeScope(next: 'personal' | 'company' | 'customer') {
    knowledgeScope.value = next;
  }

  function toggleSidebarCollapsed() {
    sidebarCollapsed.value = !sidebarCollapsed.value;
  }

  function requestNewAiSession() {
    newConversationTick.value += 1;
  }

  function openPageTab(tab: ShellPageTab) {
    let idx = pageTabs.value.findIndex((item) => item.key === tab.key);
    if (idx === -1 && typeof tab.to !== 'string') {
      const stableKey = pageTabRouteKey(tab.to);
      idx = pageTabs.value.findIndex((item) => {
        if (typeof item.to === 'string') return false;
        return pageTabRouteKey(item.to) === stableKey;
      });
    }
    // 创建页先以 ProjectCreate:（无 draft）开签，随后 replace 补上 draft 时原地升级，避免双签
    if (
      idx === -1
      && tab.key.startsWith('ProjectCreate:')
      && tab.key !== 'ProjectCreate:'
    ) {
      idx = pageTabs.value.findIndex((item) => item.key === 'ProjectCreate:');
    }
    if (idx === -1) {
      pageTabs.value.push(tab);
    } else {
      const prev = pageTabs.value[idx];
      const prevKey = prev.key;
      pageTabs.value[idx] = { ...prev, ...tab, label: tab.label || prev.label };
      if (prevKey !== tab.key) {
        if (activePageTabKey.value === prevKey) {
          activePageTabKey.value = tab.key;
        }
        migrateTabRuntimeState(prevKey, tab.key);
      }
    }
    activePageTabKey.value = tab.key;
    clearTabCompletion(tab.key);
    dashboardExpandedAiComposer.value = false;
  }

  function migrateTabRuntimeState(fromKey: string, toKey: string) {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const running = tabRunningTaskCounts.value[fromKey];
    if (running) {
      const nextCounts = { ...tabRunningTaskCounts.value };
      delete nextCounts[fromKey];
      nextCounts[toKey] = (nextCounts[toKey] ?? 0) + running;
      tabRunningTaskCounts.value = nextCounts;
    }
    if (tabCompletionKeys.value.has(fromKey)) {
      const next = new Set(tabCompletionKeys.value);
      next.delete(fromKey);
      next.add(toKey);
      tabCompletionKeys.value = next;
    }
  }

  function incrementTabRunningTasks(key: string) {
    if (!key) return;
    const cur = tabRunningTaskCounts.value[key] ?? 0;
    tabRunningTaskCounts.value = { ...tabRunningTaskCounts.value, [key]: cur + 1 };
  }

  function decrementTabRunningTasks(key: string) {
    if (!key) return;
    const cur = tabRunningTaskCounts.value[key] ?? 0;
    if (cur <= 1) {
      const next = { ...tabRunningTaskCounts.value };
      delete next[key];
      tabRunningTaskCounts.value = next;
      return;
    }
    tabRunningTaskCounts.value = { ...tabRunningTaskCounts.value, [key]: cur - 1 };
  }

  function hasTabRunningTasks(key: string) {
    return (tabRunningTaskCounts.value[key] ?? 0) > 0;
  }

  function clearTabRunningTasks(key: string) {
    if (!tabRunningTaskCounts.value[key]) return;
    const next = { ...tabRunningTaskCounts.value };
    delete next[key];
    tabRunningTaskCounts.value = next;
  }

  function markTabCompletion(key: string) {
    if (!key) return;
    clearTabRunningTasks(key);
    if (tabCompletionKeys.value.has(key)) return;
    tabCompletionKeys.value = new Set([...tabCompletionKeys.value, key]);
  }

  function clearTabCompletion(key: string) {
    if (!key || !tabCompletionKeys.value.has(key)) return;
    const next = new Set(tabCompletionKeys.value);
    next.delete(key);
    tabCompletionKeys.value = next;
  }

  function hasTabCompletion(key: string) {
    return tabCompletionKeys.value.has(key);
  }

  function closePageTab(key: string) {
    const idx = pageTabs.value.findIndex((item) => item.key === key);
    if (idx === -1) return;
    clearTabCompletion(key);
    clearTabRunningTasks(key);
    const wasActive = activePageTabKey.value === key;
    const nextTabs = pageTabs.value.filter((item) => item.key !== key);
    pageTabs.value = nextTabs;
    if (!nextTabs.length) {
      activePageTabKey.value = '';
      dashboardExpandedAiComposer.value = true;
      return;
    }
    if (!wasActive) return;
    const nextActive = nextTabs[idx - 1] || nextTabs[idx] || nextTabs[nextTabs.length - 1];
    activePageTabKey.value = nextActive?.key || '';
  }

  function reorderPageTabs(fromKey: string, toKey: string) {
    const fromIndex = pageTabs.value.findIndex((item) => item.key === fromKey);
    const toIndex = pageTabs.value.findIndex((item) => item.key === toKey);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
    const nextTabs = [...pageTabs.value];
    const [moved] = nextTabs.splice(fromIndex, 1);
    nextTabs.splice(toIndex, 0, moved);
    pageTabs.value = nextTabs;
  }

  /** 将页签移到目标下标（按移除后的最终数组下标），用于拖动实时重排 */
  function movePageTabToIndex(fromKey: string, toIndex: number) {
    const fromIndex = pageTabs.value.findIndex((item) => item.key === fromKey);
    if (fromIndex === -1) return;
    const nextTabs = [...pageTabs.value];
    const [moved] = nextTabs.splice(fromIndex, 1);
    const insertAt = Math.max(0, Math.min(toIndex, nextTabs.length));
    if (insertAt === fromIndex) {
      nextTabs.splice(fromIndex, 0, moved);
      return;
    }
    nextTabs.splice(insertAt, 0, moved);
    pageTabs.value = nextTabs;
  }

  function setActivePageTabKey(key: string) {
    activePageTabKey.value = key;
  }

  function updatePageTab(key: string, patch: Partial<Pick<ShellPageTab, 'label' | 'to'>>) {
    const idx = pageTabs.value.findIndex((item) => item.key === key);
    if (idx === -1) return;
    const cur = pageTabs.value[idx];
    pageTabs.value.splice(idx, 1, { ...cur, ...patch });
  }

  function clearPageTabs() {
    pageTabs.value = [];
    activePageTabKey.value = '';
  }

  /** 登出或切换账号时重置页签与其它 transient UI 状态 */
  function resetSessionUiState() {
    const nextKey = pageTabsStorageKey();
    const prevKey = tabsStorageKey;
    // 仅在切换到另一个已登录用户时删除旧页签存储；登出或静默重登（401 刷新失败、
    // 飞书免登 setAuth）保留存储，同一用户回到应用后 Layout 重挂载重新水合即可恢复页签。
    if (prevKey && nextKey && prevKey !== nextKey) {
      try {
        sessionStorage.removeItem(prevKey);
      } catch {
        // 浏览器禁用存储时仍清理内存状态。
      }
    }
    tabsStorageKey = null;
    pageTabs.value = [];
    activePageTabKey.value = '';
    tabCompletionKeys.value = new Set();
    tabRunningTaskCounts.value = {};
    dashboardExpandedAiComposer.value = true;
    pendingExpandedProjectDraft.value = null;
    pendingAiProjectCreateHydration.value = null;
    showProjectAutofillIntro.value = false;
    // 保持 tabsStorageKey 为空：下次 Layout 挂载时按当前用户 key 重新水合恢复页签。
  }

  function hasPageTabs() {
    return pageTabs.value.length > 0;
  }

  return {
    PRIMARY_RAIL_WIDTH,
    PRIMARY_RAIL_COLLAPSED_WIDTH,
    dashboardExpandedAiComposer,
    pendingExpandedProjectDraft,
    pendingAiProjectCreateHydration,
    showProjectAutofillIntro,
    openDashboardProjectFormTick,
    sidebarCollapsed,
    knowledgeScope,
    newConversationTick,
    pageTabs,
    restorePageTabs,
    activePageTabKey,
    tabCompletionKeys,
    tabRunningTaskCounts,
    markTabCompletion,
    clearTabCompletion,
    hasTabCompletion,
    incrementTabRunningTasks,
    decrementTabRunningTasks,
    hasTabRunningTasks,
    clearTabRunningTasks,
    setDashboardExpandedAiComposer,
    setPendingExpandedProjectDraft,
    setPendingAiProjectCreateHydration,
    startProjectAutofillIntro,
    endProjectAutofillIntro,
    requestOpenDashboardProjectForm,
    setSidebarCollapsed,
    setKnowledgeScope,
    toggleSidebarCollapsed,
    requestNewAiSession,
    openPageTab,
    reorderPageTabs,
    movePageTabToIndex,
    closePageTab,
    setActivePageTabKey,
    updatePageTab,
    clearPageTabs,
    resetSessionUiState,
    hasPageTabs,
  };
});
